import moment from "moment";
import { BRIEF_STATUS } from "../_config/quarterlyOkrConfig";

// ═══════════════════════════════════════════════════════════════════════════
// Coach brief (handoff 34) — pure helpers. The brief is AI output and only
// validated loosely by the backend: any array may be empty or partial and any
// enum may hold an unexpected string, so everything here reads defensively.
// ═══════════════════════════════════════════════════════════════════════════

export const asArray = (value) => (Array.isArray(value) ? value : []);
export const asText = (value) =>
  typeof value === "string" ? value.trim() : value == null ? "" : String(value);

// Which screen state the response is in (§3, checked in this order).
export const BRIEF_VIEW = {
  LOADING: "loading", // first GET still in flight
  PREPARING_OKRS: "preparing_okrs",
  PREPARING: "preparing",
  FAILED: "failed",
  READY: "ready",
};

export const briefViewOf = (response) => {
  if (!response) return BRIEF_VIEW.LOADING;
  if (response.preparing_okrs) return BRIEF_VIEW.PREPARING_OKRS;
  if (response.status === BRIEF_STATUS.FAILED) return BRIEF_VIEW.FAILED;
  if (response.status === BRIEF_STATUS.READY && response.brief)
    return BRIEF_VIEW.READY;
  return BRIEF_VIEW.PREPARING; // null, IN_PROGRESS, or READY without a body
};

// Poll while something is still being written (§3).
export const shouldPollBrief = (response) =>
  Boolean(response) &&
  (response.preparing_okrs ||
    response.status == null ||
    response.status === BRIEF_STATUS.IN_PROGRESS ||
    (response.status === BRIEF_STATUS.READY && !response.brief));

// "Generated 14 Oct, 10:42" in the coach's local time.
export const formatGeneratedAt = (iso) =>
  iso && moment(iso).isValid() ? moment(iso).format("D MMM, HH:mm") : "";

// ─── Chips ──────────────────────────────────────────────────────────────────
// Unknown values fall back to a grey chip with the raw text (§6).
const chipFrom = (map, value) =>
  map[value] || { tone: "grey", label: asText(value) || "Unknown" };

const FIT = {
  strong: { tone: "green", label: "Strong fit" },
  partial: { tone: "amber", label: "Partial fit" },
  weak: { tone: "red", label: "Weak fit" },
};
export const fitChip = (fit) => (fit ? chipFrom(FIT, fit) : null);

const COVERAGE = {
  covered: { tone: "green", label: "Covered" },
  deferred: { tone: "amber", label: "Deferred" },
  completed: { tone: "blue", label: "Completed" },
  no_objective: { tone: "red", label: "No objective" },
};
export const coverageChip = (status) => chipFrom(COVERAGE, status);

const TEAM = {
  submitted: { tone: "green", label: "Submitted" },
  drafted_not_submitted: { tone: "amber", label: "Draft, not submitted" },
  ai_draft_at_send: { tone: "amber", label: "AI draft at send" },
  joined_after: { tone: "grey", label: "Joined after send" },
  deactivated: { tone: "grey", label: "Deactivated" },
};
export const teamChip = (status) => chipFrom(TEAM, status);

// Problems first (§4 ⑤), then the rest, then name.
const TEAM_ORDER = [
  "drafted_not_submitted",
  "ai_draft_at_send",
  "joined_after",
  "deactivated",
  "submitted",
];
const teamRank = (status) => {
  const index = TEAM_ORDER.indexOf(status);
  return index === -1 ? 0 : index; // unknown → with the problems
};
export const sortTeam = (rows) =>
  asArray(rows)
    .filter(Boolean)
    .slice()
    .sort(
      (a, b) =>
        teamRank(a.status) - teamRank(b.status) ||
        asText(a.member).localeCompare(asText(b.member)),
    );

// ─── Objective cards (§4 ③) ─────────────────────────────────────────────────
// `chain` and `objectives` joined by String(company_objective_id), in the
// review payload's order so the cards match the Review tab; ids the review
// doesn't know go at the end, in the brief's own order.
export const buildObjectiveCards = (brief, review) => {
  const byId = new Map();
  const entry = (id) => {
    const key = String(id);
    if (!byId.has(key)) byId.set(key, { id: key, chain: null, detail: null });
    return byId.get(key);
  };
  asArray(brief?.chain).forEach((item) => {
    if (item && item.company_objective_id != null)
      entry(item.company_objective_id).chain = item;
  });
  asArray(brief?.objectives).forEach((item) => {
    if (item && item.company_objective_id != null)
      entry(item.company_objective_id).detail = item;
  });

  const reviewObjectives = asArray(review?.company_objectives);
  const titleOf = new Map(
    reviewObjectives.map((o) => [String(o.objective_id), asText(o.objective)]),
  );
  const ordered = [];
  reviewObjectives.forEach((o) => {
    const key = String(o.objective_id);
    if (byId.has(key)) {
      ordered.push(byId.get(key));
      byId.delete(key);
    }
  });
  const cards = [...ordered, ...byId.values()];

  return cards.map((card) => ({
    ...card,
    title:
      asText(card.chain?.company_objective) ||
      titleOf.get(card.id) ||
      "Untitled objective",
    inReview: titleOf.has(card.id),
  }));
};

// A brief name matched to a review member (case-insensitive, trimmed).
export const memberByName = (review) => {
  const map = new Map();
  asArray(review?.members).forEach((m) => {
    const key = asText(m?.name).toLowerCase();
    if (key) map.set(key, m);
  });
  return (name) => map.get(asText(name).toLowerCase()) || null;
};
